import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "Dividend yield is the annual dividend a company pays per share, divided by its share price, expressed as a percentage. A stock at $100 paying $3 a year in dividends has a 3% yield. Dividend yield lets you compare income across stocks the same way an interest rate lets you compare savings accounts.";

const FAQS = [
  { q: 'Is a higher dividend yield always better?', a: 'Not always. A very high yield (above 7–8%) often signals that the share price has fallen because investors expect the dividend to be cut. Always check whether the dividend is sustainable.' },
  { q: 'How often are dividends paid?', a: 'Most US companies pay quarterly. Some international companies pay semi-annually or annually. Some specialised stocks (REITs, BDCs) pay monthly.' },
  { q: 'What is a dividend payout ratio?', a: "Payout ratio = dividends ÷ earnings. It tells you how much of profit is being returned to shareholders. Above 80% can signal the dividend is at risk; under 50% gives the company room to keep paying even if earnings dip." },
  { q: 'Do all stocks pay dividends?', a: 'No. Many growth companies (Amazon, Tesla for most of its history, Berkshire Hathaway) reinvest all profits instead of paying dividends. That can be a perfectly good strategy.' },
  { q: 'Are dividends taxed?', a: 'Yes, in most countries. In the US, qualified dividends are taxed at long-term capital gains rates. Non-qualified dividends are taxed as ordinary income. Tax treatment varies by country and account type.' },
];

export default function WhatIsDividendYieldPage() {
  return (
    <BlogArticleLayout
      slug="what-is-dividend-yield"
      title="What Is Dividend Yield and How Do You Calculate It?"
      description="Dividend yield turns a company's payout into a percentage you can compare across stocks. Learn the formula and the warning signs of an unsustainable yield."
      keywords="dividend yield, what is dividend yield, dividend yield formula, how to calculate dividend yield, high dividend yield, dividend payout ratio, dividend stocks"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['what-is-eps', 'index-funds-vs-stocks', 'learn-to-invest']}
    >
      <section>
        <h2>What is dividend yield?</h2>
        <p>
          A dividend is a cash payment a company sends to shareholders, usually quarterly, out of profits.
          Dividend yield converts that payment into a percentage of the share price so you can compare it to bonds, savings accounts, or other dividend stocks.
        </p>
        <p>
          The formula: <strong>Dividend Yield = Annual Dividend per Share ÷ Share Price</strong>.
        </p>
      </section>

      <section>
        <h2>How do you calculate dividend yield?</h2>
        <p>
          You need two numbers, both shown on any stock quote page:
        </p>
        <ul>
          <li><strong>Annual dividend per share</strong> — the total dividend the company is expected to pay this year (usually the most recent quarterly dividend × 4).</li>
          <li><strong>Share price</strong> — current market price.</li>
        </ul>
        <p>
          Worked example: Coca-Cola trades at $65 and pays an annual dividend of $1.94 per share. Yield ≈ 1.94 ÷ 65 = <strong>~3.0%</strong>.
          That is comparable to a savings account, but with the upside (and downside) of stock price changes.
        </p>
      </section>

      <section>
        <h2>What's a "good" dividend yield?</h2>
        <p>
          Rough benchmarks for the US market:
        </p>
        <ul>
          <li><strong>0–1%</strong> — typical for high-growth tech stocks. Often no dividend at all.</li>
          <li><strong>1.5–3%</strong> — broad market average. Steady, dependable companies.</li>
          <li><strong>3–5%</strong> — mature dividend payers (consumer staples, utilities, telecoms).</li>
          <li><strong>5–8%</strong> — high yield. Usually REITs, BDCs, or stocks that have fallen in price.</li>
          <li><strong>Above 8%</strong> — caution. Often a signal the market expects a dividend cut.</li>
        </ul>
      </section>

      <section>
        <h2>Why can a high yield be a trap?</h2>
        <p>
          Dividend yield rises when share price falls. If a stock drops 50%, the yield doubles overnight even though nothing about the dividend itself changed.
          A "yield trap" is a stock with an enticing-looking yield that exists only because the price has collapsed on bad news, and the dividend will likely be cut next.
        </p>
        <p>
          The defence is the <strong>payout ratio</strong>: dividends paid ÷ earnings. If a company is paying out more than it earns, the dividend is borrowed time.
          Cross-check with our guide to <Link to="/blog/what-is-eps">EPS</Link> to see if the underlying earnings actually support the payout.
        </p>
      </section>

      <section>
        <h2>How does dividend yield fit a portfolio?</h2>
        <p>
          Dividends matter most for income-focused investors (retirees, FIRE-style portfolios). For young investors, total return — dividends plus price appreciation — tends to matter more,
          and many of the best long-term performers (Amazon, Berkshire) pay no dividend at all. There is no universally right answer; it depends on what you need the money to do.
        </p>
        <p>
          For a broader view of how dividends fit alongside index investing, see our guide on <Link to="/blog/index-funds-vs-stocks">index funds vs individual stocks</Link>.
        </p>
      </section>
    </BlogArticleLayout>
  );
}
